/**
 * /start shares bb.estimator with /estimate and the hero card, so answers given
 * anywhere carry over. What only the quiz asks lives in bb.start.
 */
const ESTIMATOR_KEY = "bb.estimator";
const START_KEY = "bb.start";

export interface StartAnswers {
  goal: string;
  habit: string;
  worry: string;
  state: string;
  books: string[];
  bankroll: string;
  time: string;
  ageConfirmed: boolean;
  firstName: string;
  email: string;
  entry: string;
  /** Signed token from the gate or an email. Reopens the plan on any device. */
  planToken: string;
  /** Set once the gate is passed, or the quiz finished while signed in. */
  complete: boolean;
}

export const EMPTY_ANSWERS: StartAnswers = {
  goal: "",
  habit: "",
  worry: "",
  state: "",
  books: [],
  bankroll: "",
  time: "",
  ageConfirmed: false,
  firstName: "",
  email: "",
  entry: "",
  planToken: "",
  complete: false,
};

function read(key: string): Record<string, unknown> {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) || "{}");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

export function loadAnswers(): StartAnswers {
  const shared = read(ESTIMATOR_KEY) as Partial<StartAnswers>;
  const own = read(START_KEY) as Partial<StartAnswers>;
  return {
    ...EMPTY_ANSWERS,
    ...own,
    state: shared.state || own.state || "",
    books: Array.isArray(shared.books) ? shared.books : own.books || [],
    bankroll: shared.bankroll || own.bankroll || "",
    time: shared.time || own.time || "",
  };
}

export function saveAnswers(answers: StartAnswers): void {
  const { state, books, bankroll, time, ...own } = answers;
  try {
    // The first session reads the goal from here too, so nobody answers twice.
    localStorage.setItem(
      ESTIMATOR_KEY,
      JSON.stringify({ state, books, bankroll, time, goal: answers.goal }),
    );
    localStorage.setItem(START_KEY, JSON.stringify(own));
  } catch {
    /* best-effort convenience only */
  }
}

export function clearAnswers(): void {
  try {
    localStorage.removeItem(START_KEY);
  } catch {
    /* nothing to clear */
  }
}

export const hasPlanAnswers = (a: StartAnswers) =>
  !!a.goal && !!a.bankroll && !!a.time && a.books.length > 0;

/** The shape POST /api/v1/quiz/me and the gate's quiz block expect. */
export function toQuizPayload(a: StartAnswers) {
  return {
    firstName: a.firstName || undefined,
    goal: a.goal,
    habit: a.habit || undefined,
    worry: a.worry || undefined,
    state: a.state || undefined,
    books: a.books,
    bankroll: a.bankroll,
    time: a.time,
    ageConfirmed: a.ageConfirmed,
    entry: a.entry || undefined,
  };
}

/** The address the gate took, so a sign-in link can be sent without asking twice. */
export function quizLeadEmail(): string {
  return loadAnswers().email;
}
